<div
  class="calenso-booking"
  [ngClass]="{
    'compact-layout': isCompactMode
  }"
  role="main"
  aria-label="Booking widget"
>
  @if (isWidgetRestrictionValidationFailed && this.isInternal !== true) {
    <div #calioSection id="calenso-booking" class="container-fluid">
      @if (widgetTemplates?.length) {
        <app-new-alert
          [title]="widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_RESTRICTION_WARNING_TITLE, type: 'template' } | translation: templateContent.TEMPLATE_CONTENT: _lang"
          [subTitle]="widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_RESTRICTION_WARNING_SUBTITLE, type: 'template' } | translation: templateContent.TEMPLATE_CONTENT: _lang"
          type="warning"
          customClass="mt-3 mb-3"
        />
      }
    </div>
  } @else if (isCustomerAuthenticationEnabled && this.isInternal !== true) {
    <div #calioSection id="calenso-booking" class="container-fluid">
      @if (isCustomerAuthenticationInProgress) {
        <div
          class="mt-5 d-flex align-items-center justify-content-center flex-column gap-3"
          role="status"
          aria-label="Customer authentication is in progress"
        >
          <app-loader
            customCssClass="mini-loading"
            aria-label="Customer authentication is in progress"
          />
          <p>Hold on, we're authenticating request...</p>
        </div>
      } @else if (isCustomerAuthenticationFailed) {
        <app-new-alert
          [title]="(authenticationFailedTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            ? '' : 'Customer authentication failed!'"
          [subTitle]="(authenticationFailedTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            || 'Due to invalid customer token, the request has been rejected. Please double check the URL and try again. If the problem persists, please contact your system administrator for further information.'"
          type="danger"
          customClass="mt-3 mb-3"
        />
      }
    </div>
  } @else if (isWidgetPasswordProtected && this.isInternal !== true) {
    <app-password-protection
      [supportedWidgetLanguages]="partnerGlobalData?.supported_widget_languages"
      [lang]="_lang"
      [globals]="globals"
      [langSwitcher]="langSwitcher"
      [showDevAlert]="showDevAlert"
      [widgetColorConf]="widgetColorConf"
      [widgetConf]="widgetConf"
      [customer]="customer"
      [widgetTemplates]="widgetTemplates"
      (passwordVerified)="widgetPasswordVerificationSuccess()"
      role="form"
      aria-label="Password protection"
    />
  } @else {
    <div #calioSection id="calenso-booking" class="container-fluid" role="region" aria-label="Booking content">
      <!-- Partner is blocked or deleted: the bootstrap call was refused, so no
           partner data exists. Must come first in this chain, otherwise the
           later skeleton branch renders and the widget looks stuck loading. -->
      @if (isPartnerBlocked) {
        <!-- Deliberately NOT partner-worded: this fires for a blocked OR deleted account
             (PartnerAccessHelper::isBlocked), and neither should still control what its
             own widget says. -->
        <app-new-alert
          title="{{ 'common.booking_unavailable' | translate }}"
          subTitle="{{ 'common.booking_unavailable_description' | translate }}"
          type="warning"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (!bookingWidgetMasterSettingsEnabled) {
        <!-- Two routes reach this alert and they carry the partner's wording differently:
             the success path resolves the template normally, while a 403 arrives before any
             token exists, so cakephp sends the wordings in the refusal body, keyed by locale
             (`partner_wording`) - it cannot know which language this widget is in. Picking by
             `_lang` here mirrors what the translation pipe does on the success path, and keeps
             a later language change working. Either source feeds the SAME alert inputs, so the
             text is sanitised identically whichever way it arrived. -->
        <app-new-alert
          [title]="((widgetDisabledTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang) || widgetDisabledWordings[requestedLang] || widgetDisabledWordings[_lang])
            ? '' : ('common.widget_disabled' | translate)"
          [subTitle]="((widgetDisabledTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang) || widgetDisabledWordings[requestedLang] || widgetDisabledWordings[_lang])
            || ('common.widget_disabled_description' | translate)"
          type="warning"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (adyenReturnedElsewhere) {
        <!-- The hosted card payment came back into a tab that did not start it; the tab that did
             finishes the booking. No booking flow here, so the shopper cannot pay twice. -->
        <app-new-alert
          title="{{ 'adyen.hosted.returned_elsewhere_title' | translate }}"
          subTitle="{{ 'adyen.hosted.returned_elsewhere' | translate }}"
          type="warning"
          customClass="mt-3 mb-3 cw-adyen-returned-elsewhere"
          role="alert"
          aria-live="polite"
        />
        <!-- The tab that started it may be gone (a closed opener, an in-app browser): the URL is
             already clean, so loading the page again starts over. -->
        <button
          type="button"
          class="btn btn-link p-0 cw-adyen-returned-elsewhere__restart cbw-adyen-returned-elsewhere-restart"
          (click)="startNewBooking()"
        >{{ 'adyen.hosted.start_new_booking' | translate }}</button>
      } @else if (hasBootstrapError) {
        <!-- Unknown/transient bootstrap failure (unknown 403 code / 5xx / network) —
             a distinct "something went wrong, try again later" signal so a blip
             doesn't read as the provider having switched booking off. Title carries
             the full message (like the smart-widget transient card), NOT the
             "Booking is currently unavailable" title used for the deliberate-off state. -->
        <app-new-alert
          title="{{ 'error_messages.something_went_wrong' | translate }}"
          type="warning"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (
        currentBookingType === widgetConstants.APPOINTMENT &&
        !appointmentExternalBookingEnabled &&
        this.isInternal !== true
      ) {
        <!-- Checking if external appointment booking is enabled or not -->
        <app-new-alert
          [title]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            ? '' : ('common.configuration_error' | translate)"
          [subTitle]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            || ('common.external_appointment_booking_widget_disabled' | translate)"
          type="danger"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (
        currentBookingType === widgetConstants.APPOINTMENT &&
        !appointmentInternalBookingEnabled &&
        this.isInternal === true
      ) {
        <!-- Checking if internal appointment booking is enabled or not -->
        <app-new-alert
          [title]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            ? '' : ('common.configuration_error' | translate)"
          [subTitle]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            || ('common.internal_appointment_booking_widget_disabled' | translate)"
          type="danger"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (
        currentBookingType === widgetConstants.EVENT &&
        !eventExternalBookingEnabled
      ) {
        <!-- Checking if external event booking is enabled or not -->
        <app-new-alert
          [title]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            ? '' : ('common.configuration_error' | translate)"
          [subTitle]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            || ('common.external_event_booking_widget_disabled' | translate)"
          type="danger"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else if (
        currentBookingType === widgetConstants.PRE_DEFINED_SLOT &&
        !pdsExternalBookingEnabled
      ) {
        <!-- Checking if PDS booking is enabled or not -->
        <app-new-alert
          [title]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            ? '' : ('common.configuration_error' | translate)"
          [subTitle]="(configurationErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
            || ('common.external_pds_booking_widget_disabled' | translate)"
          type="danger"
          customClass="mt-3 mb-3"
          role="alert"
          aria-live="polite"
        />
      } @else {
        @if (adyenResumeNoticeKey) {
          <app-alert
            text="{{ adyenResumeNoticeKey | translate }}"
            type="warning"
            role="alert"
            aria-live="polite"
          />
        }
        <!-- Outside the compact/non-compact split on purpose: a subscription error returns
             before partner data is fetched, so <app-compact-widget> never mounts and a
             compact link would otherwise render nothing at all. -->
        @if (showSubscriptionError) {
          <div>
            <app-new-alert
              [title]="(subscriptionErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
                ? '' : ('common.subscription_error_title' | translate)"
              [subTitle]="(subscriptionErrorTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
                || ('common.subscription_error_desc' | translate)"
              type="danger"
              customClass="mt-3 mb-3"
              role="alert"
              aria-live="polite"
            />
          </div>
        }
        @if (!isCompactMode) {
          @if (widgetConf != null && bookTypesEnabled.length > 1) {
            <header role="banner">
              <div class="row cal-bookingtype-row">
                <br/>
                <div class="col-xs-12 col-md-12">
                  <span>
                    <h4 class="switcher-title">{{ 'common.choseBookType' | translate }}</h4>
                  </span>
                </div>
              </div>
              <ng-select
                [multiple]="false"
                [items]="bookTypesEnabled"
                [(ngModel)]="bookTypesEnabled[0]"
                (change)="switchForm($event)"
                #bookSelect
                role="combobox"
                aria-labelledby="booking-type-title"
                aria-haspopup="listbox"
                [attr.aria-expanded]="bookSelect.isOpen"
              >
                <ng-template ng-label-tmp let-item="item" let-clear="clear">
                  <div>
                    {{ item | translate}}
                  </div>
                </ng-template>
                <ng-template ng-option-tmp let-item="item" let-index="index">
                  <div>
                    {{ item | translate}}
                  </div>
                </ng-template>
              </ng-select>
            </header>
          }
          @if (tokenError === false && showDevAlert) {
            <app-alert
              text="{{ 'common.devNotice' | translate }}"
              type="warning"
              role="alert"
              aria-live="polite"
            />
          }
          @if (tokenError === false && !localStorageAccessible) {
            <div>
              <app-alert
                text="{{ 'common.store_access_warning' | translate }}"
                type="warning"
                role="alert"
                aria-live="polite"
              />
            </div>
          }
          @if (currentBookingType === widgetConstants.APPOINTMENT) {
            @if (tokenError === false && partnerGlobalData && showWorkersNotAvailableError) {
              <div>
                <!-- The template's resolved text, not its presence, decides: `templates/widget`
                     always returns the row, so an empty one has to keep the standard message.
                     When there IS text it carries its own heading, so it replaces both lines. -->
                <app-new-alert
                  [title]="(nothingBookableTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
                    ? '' : ('common.workers_not_available' | translate)"
                  [subTitle]="(nothingBookableTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
                    || ('common.workers_not_available_description' | translate)"
                  type="warning"
                  customClass="mt-3 mb-3"
                  role="alert"
                  aria-live="polite"
                />
              </div>
            }
          }
          @if (!partnerGlobalData && !showSubscriptionError && tokenError === false) {
            <div>
              <div class="skeleton-stats-card mt-3">
                <div>
                  <div class="row">
                    <div class="col-md-4 mb-3 mb-md-0">
                      <div class="d-flex stats-card shadow align-items-center">
                        <div class="skeleton-animation px-3 w-100">
                          <div class="bar bar-1"></div>
                          <div class="bar bar-2"></div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 mb-3 mb-md-0">
                      <div class="d-flex stats-card shadow align-items-center">
                        <div class="skeleton-animation px-3 w-100">
                          <div class="bar bar-1"></div>
                          <div class="bar bar-2"></div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 mb-3 mb-md-0">
                      <div class="d-flex stats-card shadow align-items-center">
                        <div class="skeleton-animation px-3 w-100">
                          <div class="bar bar-1"></div>
                          <div class="bar bar-2"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="skeleton-box-card mt-4">
                <div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="d-flex stats-card shadow">
                        <div class="skeleton-animation py-5 px-5 w-100">
                          <div>
                            <div class="stat-box"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          @if (partnerGlobalData && partnerGlobalData.is_maintenance_mode_enabled === 1 && tokenError === false) {
            <div role="alert" aria-live="polite">
              <div class="d-flex flex-column justify-content-center align-items-center">
                <div>
                  <img class="maintenance-image" [src]="baseUrl + maintenanceImagePath" alt="Maintenance mode active" />
                </div>
                <div class="maintenance-title">
                  {{ 'maintenance_page_title' | translate }}
                </div>
                <div class="mt-3 maintenance-text">
                  {{ 'maintenance_page_subtitle' | translate }}
                </div>
                <div class="mt-3 maintenance-text">
                  <calenso-button
                    type="secondary"
                    id="cbw-maintenance-page-button"
                    #button
                    (clickEvent)="onMaintenancePageButton()"
                    [widgetColorConf]="widgetColorConf"
                    placement="left"
                    text="{{ 'maintenance_page_button' | translate }}"
                    aria-label="Go to maintenance page"
                  />
                </div>
              </div>
            </div>
          }
          @if (errorInConference) {
            <div>
              <app-new-alert
                subTitle="{{ errorInConference | translate }}"
                type="danger"
                customClass="mt-3 mb-3"
                role="alert"
                aria-live="polite"
              />
            </div>
          }
          @if (tokenError === false && partnerGlobalData && partnerGlobalData.is_maintenance_mode_enabled !== 1 && errorInConference === undefined) {
            @if (widgetConf) {
              <div role="main">
                @if (currentBookingType === widgetConstants.APPOINTMENT && !showWorkersNotAvailableError) {
                  @if (partnerGlobalData) {
                    <app-appointment
                      [widgetColorConf]="widgetColorConf"
                      [partner]="partnerGlobalData"
                      [globals]="globals"
                      [widgetBookingInfo]="widgetBookingInfo"
                      [lang]="_lang"
                      [isFrame]="isFrame"
                      [selectedStoreId]="selectedStoreId"
                      [selectedStoreZipCode]="selectedStoreZipCode"
                      [selectedStoreName]="selectedStoreName"
                      [selectedAppointmentServiceIds]="selectedAppointmentServiceIds"
                      [selectedAppointmentServiceCategoryIds]="selectedAppointmentServiceCategoryIds"
                      [selectedWorkerId]="selectedWorkerId"
                      [selectedWorkerIds]="selectedWorkerIds"
                      [selectedJsonCustomFields]="selectedJsonCustomFields"
                      [selectedJsonCustomerDetail]="selectedJsonCustomerDetail"
                      [calendarPreselectedDate]="calendarPreselectedDate"
                      [dateContext]="dateContext"
                      [dateContextStart]="dateContextStart"
                      [dateContextEnd]="dateContextEnd"
                      [widgetConf]="widgetConf"
                      [bookerWorkerId]="bookerWorkerId"
                      [utmSource]="utmSource"
                      [utmMedium]="utmMedium"
                      [utmContent]="utmContent"
                      [utmCampaign]="utmCampaign"
                      [utmTerm]="utmTerm"
                      [utmId]="utmId"
                      [isBookingDisabled]="isBookingDisabled"
                      [foundedWorker]="foundedWorker"
                      [meeting_type_id]="meeting_type_id"
                      [customer_notification_preference]="customer_notification_preference"
                      [internalComment]="internalComment"
                      [langSwitcher]="langSwitcher"
                      [conferenceState]="conferenceState"
                      [hideStoreSelection]="hideStoreSelection"
                      [skipQualificationQuestion]="skipQualificationQuestion"
                      [bookingType]="bookingType"
                      [customer]="customer"
                      [widgetTemplates]="widgetTemplates"
                      [preApplyCoupon]="coupon"
                      [timezones]="timezones"
                      [redirectedPaymentIntent]="redirectedPaymentIntent"
                      [redirectedPaymentIntentClientSecret]="redirectedPaymentIntentClientSecret"
                      [appointmentStripeRedirectionData]="appointmentStripeRedirectionData"
                      [adyenResume]="adyenResumeFor('appointment')"
                      [customerProfileUuid]="customerProfileUuid"
                      [selectedJsonBrokerDetail]="selectedJsonBrokerDetail"
                      [enteredAppointmentFromTour]="enteredAppointmentFromTour"
                      [prefetchedTourCombiService]="prefetchedTourCombiService"
                      (bookingSuccessEvent)="onBookingSuccessfull()"
                      (backToTourEvent)="onBackToTour($event)"
                      role="form"
                      aria-label="Appointment booking"
                    />
                  }
                }
                @if (currentBookingType === widgetConstants.EVENT) {
                  @if (partnerGlobalData) {
                    <app-event
                      [widgetColorConf]="widgetColorConf"
                      [partner]="partnerGlobalData"
                      [globals]="globals"
                      [widgetBookingInfo]="widgetBookingInfo"
                      [lang]="_lang"
                      [selectedJsonCustomFields]="selectedJsonCustomFields"
                      [selectedJsonCustomerDetail]="selectedJsonCustomerDetail"
                      [filterStores]="filterStores"
                      [filterCategories]="filterCategories"
                      [widgetConf]="widgetConf"
                      [utmSource]="utmSource"
                      [utmMedium]="utmMedium"
                      [utmContent]="utmContent"
                      [utmCampaign]="utmCampaign"
                      [utmTerm]="utmTerm"
                      [utmId]="utmId"
                      [meeting_type_id]="meeting_type_id"
                      [customer_notification_preference]="customer_notification_preference"
                      [uiMode]="uiMode"
                      [hideGridSwitcher]="hideGridSwitcher"
                      [langSwitcher]="langSwitcher"
                      [preApplyCoupon]="coupon"
                      [timezones]="timezones"
                      [redirectedPaymentIntent]="redirectedPaymentIntent"
                      [redirectedPaymentIntentClientSecret]="redirectedPaymentIntentClientSecret"
                      [eventStripeRedirectionData]="eventStripeRedirectionData"
                      [adyenResume]="adyenResumeFor('event')"
                      role="form"
                      aria-label="Event booking"
                      [customer]="customer"
                      [widgetTemplates]="widgetTemplates"
                    />
                  }
                }
                @if (currentBookingType === widgetConstants.PRE_DEFINED_SLOT) {
                  @if (partnerGlobalData) {
                    <app-pre-defined-slots
                      [widgetColorConf]="widgetColorConf"
                      [partner]="partnerGlobalData"
                      [globals]="globals"
                      [lang]="_lang"
                      [widgetConf]="widgetConf"
                      [selectedJsonCustomerDetail]="selectedJsonCustomerDetail"
                      [selectedJsonCustomFields]="selectedJsonCustomFields"
                      [selectedAppointmentServiceIds]="selectedAppointmentServiceIds"
                      [customer_notification_preference]="customer_notification_preference"
                      [internalComment]="internalComment"
                      [bookerWorkerId]="bookerWorkerId"
                      [langSwitcher]="langSwitcher"
                      [utmSource]="utmSource"
                      [utmMedium]="utmMedium"
                      [utmContent]="utmContent"
                      [utmCampaign]="utmCampaign"
                      [utmTerm]="utmTerm"
                      [widgetTemplates]="widgetTemplates"
                      [customer]="customer"
                      [redirectedPaymentIntent]="redirectedPaymentIntent"
                      [redirectedPaymentIntentClientSecret]="redirectedPaymentIntentClientSecret"
                      [pdsStripeRedirectionData]="pdsStripeRedirectionData"
                      [adyenResume]="adyenResumeFor('pds')"
                      [preApplyCoupon]="coupon"
                      (bookingSuccessEvent)="onBookingSuccessfull()"
                      role="form"
                      aria-label="Pre-defined slot booking"
                    />
                  }
                }
                @if (currentBookingType === widgetConstants.TOUR) {
                  @if (partnerGlobalData && tourExternalBookingEnabled) {
                    <app-tour
                      [storeId]="selectedStoreId || 0"
                      [maxDurationMinutes]="(partnerGlobalData.dynamic_combi_max_duration_minutes || 180)"
                      [initialSelectedUuids]="tourLastSelectedUuids"
                      [widgetTemplates]="widgetTemplates"
                      [lang]="_lang"
                      [supportedWidgetLanguages]="partnerGlobalData?.supported_widget_languages"
                      [langSwitcher]="langSwitcher"
                      [globals]="globals"
                      (combiPrepared)="onTourCombiPrepared($event)"
                    />
                  } @else {
                    <app-new-alert
                      [subTitle]="(tourUnavailableTemplate | translation: templateContent.TEMPLATE_CONTENT: _lang)
                        || ('tour_widget.error_feature_not_available' | translate)"
                      type="danger"
                      customClass="mt-3 mb-3"
                      role="alert"
                      aria-live="polite"
                    />
                  }
                }
                @if (currentBookingType === widgetConstants.SUCCESS) {
                  <div role="status" aria-live="polite">
                    @if (partnerGlobalData) {
                      <app-finalpage
                        [partner]="partnerGlobalData"
                        [success]="true"
                        />
                    }
                  </div>
                }
                @if (currentBookingType === widgetConstants.ERROR) {
                  <div role="alert" aria-live="polite">
                    @if (partnerGlobalData) {
                      <app-finalpage
                        [partner]="partnerGlobalData"
                        [success]="false"
                        />
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="init-container"></div>
            }
          }
          @if (tokenError === true) {
            <div>
              <app-new-alert
                title="{{ 'common.configuration_error' | translate }}"
                type="danger"
                customClass="mt-3 mb-3"
                [subTitle]="tokenErrorMessage | translate"
                role="alert"
                aria-live="polite"
              />
            </div>
          }
        } @else if (isCompactMode) {
          @if (partnerGlobalData) {
            <app-compact-widget
              [calendarPreselectedDate]="calendarPreselectedDate"
              [dateContext]="dateContext"
              [dateContextStart]="dateContextStart"
              [dateContextEnd]="dateContextEnd"
              [globals]="globals"
              [lang]="_lang"
              [isFrame]="isFrame"
              [langSwitcher]="langSwitcher"
              [partner]="partnerGlobalData"
              [selectedAppointmentServiceIds]="selectedAppointmentServiceIds"
              [customer_notification_preference]="customer_notification_preference"
              [selectedStoreId]="selectedStoreId"
              [selectedJsonCustomerDetail]="selectedJsonCustomerDetail"
              [selectedJsonCustomFields]="selectedJsonCustomFields"
              [showSubscriptionError]="showSubscriptionError"
              [widgetColorConf]="widgetColorConf"
              [worker]="foundedWorker"
              [bookerWorkerId]="bookerWorkerId"
              [utmSource]="utmSource"
              [utmMedium]="utmMedium"
              [utmContent]="utmContent"
              [utmCampaign]="utmCampaign"
              [utmTerm]="utmTerm"
              [utmId]="utmId"
              [isBookingDisabled]="isBookingDisabled"
              [widgetConf]="widgetConf"
              [debug]="debug"
              [token]="token"
              [widgetBookingInfo]="widgetBookingInfo"
              [bookingType]="bookingType"
              [preApplyCoupon]="coupon"
              [widgetTemplates]="widgetTemplates"
              [timezones]="timezones"
              [redirectedPaymentIntent]="redirectedPaymentIntent"
              [redirectedPaymentIntentClientSecret]="redirectedPaymentIntentClientSecret"
              [appointmentStripeRedirectionData]="appointmentStripeRedirectionData"
              [adyenResume]="adyenResumeFor('compact')"
              role="form"
              aria-label="Compact booking widget"
            />
          }
        }
      }
    </div>
  }

  @if (analytics) {
    <div role="complementary" aria-hidden="true">
      @if (analytics.GTM_ENABLED && analytics.GTM && analytics?.GTM.trim() !== '') {
        <app-gtm-operation
          [gtmID]="analytics.GTM"
          />
      }
    </div>
  }
</div>
